import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import { ReactKeycloakProvider } from '@react-keycloak/web';
import keycloak from "./utils/keycloakauth.js";
import { AdminProvider } from './context/AdminContext';
import { unregisterServiceWorkers } from './utils/unregisterServiceWorker.js';
import { BYPASS_AUTH } from './utils/config.js';
import KeycloakErrorBoundary from './components/KeycloakErrorBoundary.jsx';
import './index.css'
// Import i18n configuration
import './i18n.js'
import 'react-toastify/dist/ReactToastify.css';

// Unregister any existing service workers
unregisterServiceWorkers().then(() => {
  console.log('Service worker cleanup completed');
});

// Standard Keycloak init – redirect to login if no valid session exists.
// This matches the configuration used in other Supervity projects with the
// same Keycloak instance / confidential client.
const keycloakConfig = {
  onLoad: 'login-required',
  pkceMethod: 'S256',
  checkLoginIframe: false,
  flow: 'standard',
  enableLogging: false,
  minTokenValidity: 120,
};

// Event handler for Keycloak events
const handleKeycloakEvent = (event, error) => {
  console.log('🔐 Keycloak event:', event, error || 'No error');

  // Enhanced logging for common errors
  if (error) {
    console.error('❌ Keycloak error details:', {
      event,
      error: error.toString(),
      errorType: error?.error,
      errorDescription: error?.error_description,
      timestamp: new Date().toISOString()
    });

    // Check for CSP-related errors
    if (error?.error?.includes('Timeout') || error?.error?.includes('iframe')) {
      console.warn('⚠️ CSP/Iframe issue detected. Check KEYCLOAK_CSP_DEBUG.md for troubleshooting.');
    }
  }
};

// Event handler for Keycloak token changes
const handleKeycloakTokens = (tokens) => {
  console.log('Keycloak tokens changed', tokens ? 'Token available' : 'No token');
};

// Make sure the root element exists
const container = document.getElementById('root');
if (!container) {
  throw new Error('Failed to find the root element');
}

// Create the root instance
const root = ReactDOM.createRoot(container);

// Render the app with proper error boundaries
// When VITE_BYPASS_AUTH=true (local dev), skip Keycloak entirely to avoid CORS
// errors from the production SSO server blocking requests from localhost.
if (BYPASS_AUTH) {
  console.log('🔓 Auth bypass enabled (VITE_BYPASS_AUTH=true) — skipping Keycloak init');
  root.render(
    <React.StrictMode>
      <AdminProvider>
        <App />
      </AdminProvider>
    </React.StrictMode>
  );
} else {
  root.render(
    <KeycloakErrorBoundary>
      <ReactKeycloakProvider
        authClient={keycloak}
        initOptions={keycloakConfig}
        onEvent={handleKeycloakEvent}
        onTokens={handleKeycloakTokens}
      >
        <React.StrictMode>
          <AdminProvider>
            <App />
          </AdminProvider>
        </React.StrictMode>
      </ReactKeycloakProvider>
    </KeycloakErrorBoundary>
  );
}